import React, { useCallback, useEffect, useState, useRef } from 'react';

import { RightOutlined } from '@ant-design/icons';
import { Button, Drawer as DrawerAntD, Input, Radio, Result, Select, Table } from 'antd';
import { T, TF } from 'react-translator-component';
import styled from 'styled-components';

import {
  heartbeatLabel,
  issueLabel,
  issueSeverity,
  sortedIssues,
  SEVERITY_STYLE,
  stationLocation,
  stationTitle,
  worstSeverity,
} from './stationIssues';
import { StationRegistrationPanel } from './StationRegistrationPanel';
import { StationsMap } from './StationsMap';


import {
  getIntegrationStations,
  getIntegrationStationTotals,
} from '../../../../hooks/interoperability';
import {
  Connection,
  ContractDirection,
  IntegrationStation,
  IntegrationStationTotals,
  StationIssue,
  StationQuery,
  StationRegistration,
} from '../../../../types/responses/Interoperability';
import { StationDrawerDetails } from '../../../molecules/InteroperabilityDrawers';
import { ListSkeleton } from '../../../molecules/ListSkeleton/ListSkeleton';
import { Pagination } from '../../../molecules/Pagination/Pagination';
import { directionLabel } from '../contractRules';
import { CellSub, Chevron, ClickableRows, FilterBar, FilterCount, FilterSpacer, ListCard, ViewSwitch } from '../styled';
import { formatCount } from '../transactionFormat';

/** O Drawer do antd não declara children no typing desta versão. */
const Drawer: any = DrawerAntD;

const Note = styled.p`
  margin: 0 0 8px;
  font-size: 12px;
  color: #6b7280;
`;

const Summary = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`;

const SummaryCard = styled.button<{ selected: boolean }>`
  flex: 1 1 150px;
  text-align: left;
  background: #fff;
  border: 1px solid ${({ selected }) => (selected ? '#8a9099' : '#e9ebee')};
  border-radius: 8px;
  padding: 10px 14px;
  cursor: pointer;
`;

const SummaryKey = styled.span`
  display: block;
  font-size: 11px;
  color: #8a9099;
`;

const SummaryValue = styled.strong`
  font-size: 18px;
  color: #2d3339;
  font-variant-numeric: tabular-nums;
`;

const Name = styled.div`
  color: #2d3339;
`;

const Tag = styled.span<{ bg: string; fg: string }>`
  display: inline-block;
  background: ${({ bg }) => bg};
  color: ${({ fg }) => fg};
  border-radius: 999px;
  padding: 2px 9px;
  font-size: 11px;
  margin: 0 4px 4px 0;
  white-space: nowrap;
`;

const Ok = styled.span`
  color: #1e7a45;
  font-size: 11.5px;
`;

/**
 * A barra vermelha na estacao que o parceiro nao consegue usar.
 *
 * Barra na lateral, e nao fundo tingido: com metade da lista com problema — o que é o
 * caso real de hoje — fundo colorido em toda linha deixa de destacar nada.
 */
const Rows = styled(ClickableRows)`
  .station-blocking td:first-child {
    box-shadow: inset 3px 0 0 #c8352f;
  }
`;

const MapNote = styled.p`
  background: #fff;
  border: 1px solid #e3e6ea;
  border-radius: 8px;
  padding: 8px 12px;
  margin: 0 0 8px;
  font-size: 11.5px;
  color: #4a5462;
`;

/**
 * Dez por pagina.
 *
 * A linha da estacao tem quatro informacoes empilhadas — nome e conta, contrato e
 * parceiros, local e conectores, sinal — mais as etiquetas de problema. Vinte e cinco
 * dessas nao se leem: a pagina fica com mais de mil pixels de altura e a rolagem substitui
 * a paginacao.
 */
const PAGE_SIZE = 10;

/**
 * Quantas estacoes o mapa carrega de uma vez.
 *
 * O mapa nao pagina: um pino por estacao do filtro, ou o mapa mentiria por omissao.
 * Acima disto a tela diz quantas ficaram de fora, em vez de cortar em silencio.
 *
 * Era 500, e com 529 estacoes no banco de dev o corte tirava 29 linhas — entre elas
 * pinos, porque a ordem poe as com problema na frente e nao as com coordenada. Mil
 * cobre a base de dev inteira; num tenant maior o aviso continua dizendo a verdade.
 */
const MAP_LIMIT = 1000;

/**
 * Janela de espera da busca por texto.
 *
 * O campo dispara onChange a cada tecla, e cada consulta sai em duas requisicoes (pagina e
 * totais): "floripa" mandava catorze. Exportado porque o teste espera esta janela, e um
 * numero repetido nos dois lados envelhece torto.
 */
export const SEARCH_DEBOUNCE_MS = 300;

export type StationsTabProps = {
  tenantId: string;

  /** Parceiros com contrato, para o filtro. */
  connections: Connection[];
};

/**
 * Aba Estações (MV2-1706).
 *
 * Responde o que o parceiro realmente vê, de qual contrato cada estação vem e qual
 * delas vai chegar quebrada do outro lado. As outras abas falam de contrato; esta é a
 * única que fala do que o contrato entrega.
 *
 * O filtro e a paginação são do servidor, e os totais do cabeçalho vêm de consulta
 * própria sobre o filtro inteiro: contar a página faria "525 sem coordenada" virar
 * "25" ao mudar de página.
 */
export const StationsTab: React.FC<StationsTabProps> = ({ tenantId, connections }) => {
  /**
   * `search` e o que esta na caixa; `searchApplied` e o que foi para a consulta.
   *
   * Sao dois estados porque o campo precisa responder a cada tecla enquanto a consulta
   * espera a pessoa parar de digitar.
   */
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [direction, setDirection] = useState<ContractDirection | ''>('');
  const [partnerTenantId, setPartnerTenantId] = useState('');
  const [issue, setIssue] = useState<StationIssue | ''>('');
  const [registration, setRegistration] = useState<StationRegistration | ''>('');

  const [view, setView] = useState<'list' | 'map'>('list');
  const [stations, setStations] = useState<IntegrationStation[]>([]);
  const [totals, setTotals] = useState<IntegrationStationTotals | null>(null);
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  /**
   * `loading` e so a primeira carga, e por isso nunca volta a ser verdadeiro.
   *
   * O galho do mapa ficava atras dele: a cada recarga o GoogleMap desmontava e voltava
   * zerado, e quem tinha dado zoom num bairro perdia o enquadramento a cada tecla. A
   * recarga agora aparece como `reloading`, que a tabela mostra como spinner sem
   * desmontar nada.
   */
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [detail, setDetail] = useState<IntegrationStation | null>(null);

  const requestId = useRef(0);

  const query: StationQuery = {
    ...(searchApplied ? { search: searchApplied } : {}),
    ...(direction ? { direction } : {}),
    ...(partnerTenantId ? { partnerTenantId } : {}),
    ...(issue ? { issue } : {}),
    ...(registration ? { registration } : {}),
  };

  const queryKey = JSON.stringify(query);

  // Espera a pessoa parar de digitar antes de consultar. O clearTimeout no retorno e o
  // que faz a tecla seguinte substituir a espera anterior em vez de somar outra.
  useEffect(() => {
    const timer = setTimeout(() => setSearchApplied(search), SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    // Numero da consulta: a resposta so pinta a tela se for a ultima pedida. Sem isto, a
    // resposta de um filtro antigo chegando depois sobrescrevia a atual — e como o
    // `finally` dela tambem desligava o loading, nao havia nem sinal de que a tela estava
    // mostrando outra coisa.
    const id = requestId.current + 1;
    requestId.current = id;

    try {
      setReloading(true);
      setLoadError(false);

      const parsed: StationQuery = JSON.parse(queryKey);

      // No mapa a pagina nao existe: os pinos sao do filtro inteiro, ate o limite.
      const [pageResult, totalsResult] = await Promise.all([
        view === 'map'
          ? getIntegrationStations(tenantId, parsed, 0, MAP_LIMIT)
          : getIntegrationStations(tenantId, parsed, page, PAGE_SIZE),
        getIntegrationStationTotals(tenantId, parsed),
      ]);

      if (id !== requestId.current) {
        return;
      }

      setStations(pageResult?.content || []);
      setTotalElements(pageResult?.totalElements || 0);
      setTotals(totalsResult);
    } catch {
      if (id === requestId.current) {
        setLoadError(true);
      }
    } finally {
      if (id === requestId.current) {
        setReloading(false);
        // A primeira carga terminou, com erro ou sem: dai em diante a tela fica montada.
        setLoading(false);
      }
    }
  }, [tenantId, queryKey, page, view]);

  useEffect(() => {
    load();
  }, [load]);

  /** Trocar filtro volta para a primeira página: a atual pode não existir mais. */
  const changeFilter = (apply: () => void) => {
    apply();
    setPage(0);
  };

  /**
   * Quantas do filtro nao tem coordenada.
   *
   * Vem dos totais, que sao do filtro inteiro, e nao da pagina carregada: no mapa a
   * conta precisa bater com o que ficou de fora dele.
   */
  const semCoordenada = totals?.by_issue?.MISSING_COORDINATES ?? 0;

  const columns = [
    {
      title: T('Station'),
      key: 'station',
      render: (_: unknown, station: IntegrationStation) => (
        <div>
          <Name>{stationTitle(station)}</Name>
          <CellSub>{station.owner_name || '—'}</CellSub>
        </div>
      ),
    },
    {
      title: T('Contract'),
      key: 'contract',
      render: (_: unknown, station: IntegrationStation) => (
        <div>
          <Name>{directionLabel(station.direction)}</Name>
          {/* De qual contrato a estacao vem: a pergunta que abre a aba. */}
          <CellSub>
            {station.partners && station.partners.length > 0
              ? station.partners.map((partner) => partner.name).join(', ')
              : T('none')}
          </CellSub>
        </div>
      ),
    },
    {
      title: T('Location'),
      key: 'location',
      render: (_: unknown, station: IntegrationStation) => (
        <div>
          <Name>{stationLocation(station)}</Name>
          <CellSub>{station.connector_count ?? 0} {T('connectors')}</CellSub>
        </div>
      ),
    },
    {
      title: T('Last signal'),
      key: 'signal',
      render: (_: unknown, station: IntegrationStation) => (
        <div>
          <Name>{heartbeatLabel(station)}</Name>
          <CellSub>{station.online ? T('Online') : T('Offline')}</CellSub>
        </div>
      ),
    },
    {
      title: T('Registration'),
      key: 'issues',
      render: (_: unknown, station: IntegrationStation) => {
        const issues = sortedIssues(station.issues || []);

        if (issues.length === 0) {
          return <Ok>✓ {T('Correct')}</Ok>;
        }

        return (
          <div>
            {issues.map((each) => {
              const style = SEVERITY_STYLE[issueSeverity(each)];

              return (
                <Tag key={each} bg={style.bg} fg={style.fg}>
                  {issueLabel(each)}
                </Tag>
              );
            })}
          </div>
        );
      },
    },
    {
      /* A seta que avisa que a linha abre detalhe, como Conexoes e API. */
      title: '',
      key: 'open',
      width: 40,
      render: () => (
        <Chevron>
          <RightOutlined />
        </Chevron>
      ),
    },
  ];

  /**
   * Os cartoes falam de contrato: o total, o que eu compartilho e o que eu recebo.
   *
   * Cadastro saiu daqui e virou area propria — dois cartoes, uma opcao de filtro e uma
   * frase com link no mapa eram tres caminhos para a mesma coisa.
   */
  const summaryCards: { key: string; label: string; value: number; apply: () => void; selected: boolean }[] = [
    {
      key: 'all',
      label: TF('Available stations'),
      value: totals?.station_count ?? 0,
      apply: () => changeFilter(() => {
        setDirection('');
        setIssue('');
        setRegistration('');
      }),
      selected: !direction && !issue && !registration,
    },
    {
      key: 'shared',
      label: `${directionLabel('SHARING')}`,
      value: totals?.shared_count ?? 0,
      apply: () => changeFilter(() => setDirection('SHARING')),
      selected: direction === 'SHARING',
    },
    {
      key: 'received',
      label: `${directionLabel('RECEIVING')}`,
      value: totals?.received_count ?? 0,
      apply: () => changeFilter(() => setDirection('RECEIVING')),
      selected: direction === 'RECEIVING',
    },
  ];

  return (
    <Rows>
      <Note>
        {T(
          'Every station reached by an active contract, in both directions, with the contract it belongs to and what would reach the partner broken.',
        )}
      </Note>

      {/* Os cartões também filtram: o número que assusta é o caminho para a lista dele. */}
      <Summary>
        {summaryCards.map((card) => (
          <SummaryCard key={card.key} type="button" selected={card.selected} onClick={card.apply}>
            <SummaryKey>{card.label}</SummaryKey>
            <SummaryValue>{formatCount(card.value)}</SummaryValue>
          </SummaryCard>
        ))}
      </Summary>

      <FilterBar>
        <Input.Search
          allowClear
          style={{ maxWidth: 240 }}
          placeholder={TF('Search by name or number')}
          value={search}
          onChange={(event) => changeFilter(() => setSearch(event.target.value))}
        />

        <Select
          value={direction}
          style={{ minWidth: 170 }}
          onChange={(value: ContractDirection | '') => changeFilter(() => setDirection(value))}
        >
          <Select.Option value="">{T('Any contract')}</Select.Option>
          <Select.Option value="SHARING">{directionLabel('SHARING')}</Select.Option>
          <Select.Option value="RECEIVING">{directionLabel('RECEIVING')}</Select.Option>
        </Select>

        <Select
          value={partnerTenantId}
          style={{ minWidth: 190 }}
          showSearch
          optionFilterProp="children"
          onChange={(value: string) => changeFilter(() => setPartnerTenantId(value))}
        >
          <Select.Option value="">{T('All partners')}</Select.Option>
          {connections.map((connection) => (
            <Select.Option key={connection.partner_tenant_id} value={connection.partner_tenant_id}>
              {connection.partner_name}
            </Select.Option>
          ))}
        </Select>

        <FilterSpacer />

        <FilterCount>
          {formatCount(totalElements)} {T('stations')}
        </FilterCount>

        {/*
          Lista e mapa respondem perguntas diferentes sobre o mesmo filtro: "quais sao" e
          "onde estao". O filtro nao se perde na troca — e o mesmo estado.
        */}
        <ViewSwitch
          value={view}
          size="small"
          buttonStyle="solid"
          onChange={(event) => setView(event.target.value)}
        >
          <Radio.Button value="list">{T('List')}</Radio.Button>
          <Radio.Button value="map">{T('Map')}</Radio.Button>
        </ViewSwitch>
      </FilterBar>

      {/*
        Depois do filtro, e nao antes: o filtro e o que se mexe primeiro, e as contagens
        daqui sao do filtro corrente — ler o numero antes de escolher o recorte inverte a
        ordem em que a tela e usada.
      */}
      <StationRegistrationPanel
        totals={totals}
        selectedIssue={issue}
        onlyCorrect={registration === 'WITHOUT_ISSUES' && !issue}
        onSelectIssue={(each) =>
          changeFilter(() => {
            // Clicar no que ja esta escolhido limpa. Com o combo fora da barra, e daqui que
            // se sai do recorte — sem isso o filtro viraria armadilha de mao unica.
            setIssue(issue === each ? '' : each);
            setRegistration('');
            // Lista, e nao mapa: a estacao sem coordenada — a maioria dos casos — nao
            // aparece no mapa, e cair num mapa vazio foi o que confundiu antes.
            setView('list');
          })
        }
        onClear={() =>
          changeFilter(() => {
            setIssue('');
            setRegistration('');
          })
        }
        onSelectCorrect={() =>
          changeFilter(() => {
            setIssue('');
            setRegistration(registration === 'WITHOUT_ISSUES' ? '' : 'WITHOUT_ISSUES');
          })
        }
      />


      {loadError && (
        <Result
          status="warning"
          title={T('Could not load the stations.')}
          extra={
            <Button type="primary" onClick={load}>
              {T('Try again')}
            </Button>
          }
        />
      )}

      {loading && <ListSkeleton />}

      {!loading && !loadError && view === 'map' && (
        <>
          {/*
            Quantas estacoes o mapa nao consegue mostrar. Sem isto o mapa mente por
            omissao: hoje, no banco de dev, 381 das 529 nao tem coordenada, e um mapa
            magro pareceria "nao ha estacao".

            So informa. Quem quer agir vai na area de cadastro, acima — o link aqui dentro
            misturava as duas coisas.
          */}
          {semCoordenada > 0 && (
            <MapNote>
              {semCoordenada} {T('of the filtered stations have no coordinates and do not appear on the map.')}
            </MapNote>
          )}

          {totalElements > MAP_LIMIT && (
            <MapNote>
              {T('Showing the first')} {MAP_LIMIT} {T('of')} {formatCount(totalElements)}{' '}
              {T('stations in this filter.')}
            </MapNote>
          )}

          <StationsMap stations={stations} onOpen={(station) => setDetail(station)} />
        </>
      )}

      {!loading && !loadError && view === 'list' && (
        <ListCard>
          <Table
            loading={reloading}
            rowKey="charge_box_id"
            locale={{ emptyText: T('No station matches this filter.') }}
            pagination={false}
            columns={columns}
            dataSource={stations}
            scroll={{ x: true }}
            rowClassName={(station: IntegrationStation) =>
              // As duas classes na mesma funcao: o antd aceita um rowClassName so, e
              // declarar o atributo duas vezes descarta silenciosamente o primeiro.
              worstSeverity(station.issues || []) === 'BLOCKING'
                ? 'interop-row station-blocking'
                : 'interop-row'
            }
            onRow={(item: IntegrationStation) => ({
              onClick: () => setDetail(item),
            })}
          />

          <Pagination
            currentPage={page + 1}
            pageSize={PAGE_SIZE}
            totalCount={totalElements}
            onPageChange={(next: number) => setPage(next - 1)}
          />
        </ListCard>
      )}

      <Drawer
        width={420}
        visible={detail !== null}
        title={detail ? stationTitle(detail) : ''}
        onClose={() => setDetail(null)}
      >
        {detail && <StationDrawerDetails tenantId={tenantId} station={detail} />}
      </Drawer>
    </Rows>
  );
};
